import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { isSystemAdmin } from '../../features/profile';
import { getRoles, type Role } from '../../features/roles';
import { getRolePermissions, putRolePermissions } from '../../features/permissions';
import type { EffectivePermissions, FeatureKey, PermissionLevel } from '../../features/permissions/types';
import { FEATURE_KEYS } from '../../features/permissions/types';
import { defaultEffectivePermissions } from '../../features/permissions/lib';
import { Card, DashboardPageHeader } from '../../shared/ui';
import { ApiError } from '../../shared/api';
import './RolePermissionsPage.css';

const SYSTEM_ROLE_NAME_ADMIN = 'ADMIN';

const FEATURE_LABELS: Record<FeatureKey, string> = {
  dashboard: 'Dashboard',
  events: 'Events',
  projects: 'Projects',
  committee: 'Committee',
  timeline: 'Timeline',
  gallery: 'Gallery',
  reviews: 'Reviews',
  notices: 'Notices',
  classes: 'Classes',
  contactSettings: 'Contact settings',
  users: 'Users',
  roles: 'Roles',
  telemetry: 'Traffic / telemetry',
  systemHealth: 'System health',
  blog: 'Blog',
  media: 'Media / images',
};

type AccessTier = 'off' | 'access' | 'admin';

type ActionFlags = { create: boolean; edit: boolean; delete: boolean };

/** Checkbox state is stored separately from draft so create/edit can differ while level stays `edit`. */
type PermUiState = {
  draft: EffectivePermissions;
  /** Source of truth for checkboxes; synced from API on load only (not after each edit→draft round-trip). */
  actions: Record<FeatureKey, ActionFlags>;
};

function defaultFeatureActions(): Record<FeatureKey, ActionFlags> {
  const o = {} as Record<FeatureKey, ActionFlags>;
  for (const k of FEATURE_KEYS) {
    o[k] = { create: false, edit: false, delete: false };
  }
  return o;
}

/** Initial checkbox map from API (lossy for `edit`: defaults both on until user changes). */
function actionsFromDraft(d: EffectivePermissions): Record<FeatureKey, ActionFlags> {
  const o = {} as Record<FeatureKey, ActionFlags>;
  for (const k of FEATURE_KEYS) {
    o[k] = levelToUi(d[k]).actions;
  }
  return o;
}

/** Access tier: checkboxes only — view/read is implicit when no action is checked */
function actionsToLevel(actions: ActionFlags): 'view' | 'edit' | 'delete' {
  if (actions.delete) return 'delete';
  if (actions.create || actions.edit) return 'edit';
  return 'view';
}

function levelToUi(level: PermissionLevel): { tier: AccessTier; actions: ActionFlags } {
  if (level === 'none') {
    return { tier: 'off', actions: { create: false, edit: false, delete: false } };
  }
  if (level === 'admin') {
    return { tier: 'admin', actions: { create: false, edit: false, delete: false } };
  }
  if (level === 'view') {
    return { tier: 'access', actions: { create: false, edit: false, delete: false } };
  }
  if (level === 'edit') {
    return { tier: 'access', actions: { create: true, edit: true, delete: false } };
  }
  return { tier: 'access', actions: { create: true, edit: true, delete: true } };
}

function uiToLevel(tier: AccessTier, actions: ActionFlags): PermissionLevel {
  if (tier === 'off') return 'none';
  if (tier === 'admin') return 'admin';
  return actionsToLevel(actions);
}

function applyFeature(
  prev: PermUiState,
  key: FeatureKey,
  tier: AccessTier,
  actions: ActionFlags
): PermUiState {
  const level = uiToLevel(tier, actions);
  return {
    draft: { ...prev.draft, [key]: level },
    actions: { ...prev.actions, [key]: actions },
  };
}

const RolePermissionsPage: React.FC = () => {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [perm, setPerm] = useState<PermUiState>(() => ({
    draft: defaultEffectivePermissions(),
    actions: defaultFeatureActions(),
  }));
  const [loadingList, setLoadingList] = useState(true);
  const [loadingPerm, setLoadingPerm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allowed = isSystemAdmin(profile);

  const selectedRole = roles.find((r) => r.id === selectedId) ?? null;
  const isAdminRole = selectedRole?.name === SYSTEM_ROLE_NAME_ADMIN;

  const loadRoles = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const list = await getRoles();
      setRoles(list);
      setSelectedId((prev) => {
        if (prev && list.some((r) => r.id === prev)) return prev;
        return list[0]?.id ?? null;
      });
    } catch (err) {
      console.error(err);
      setError('Failed to load roles');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) void loadRoles();
  }, [allowed, loadRoles]);

  useEffect(() => {
    if (!selectedId || !allowed) return;
    let cancelled = false;
    (async () => {
      setLoadingPerm(true);
      setError(null);
      try {
        const res = await getRolePermissions(selectedId);
        if (cancelled) return;
        setPerm({
          draft: res.effectivePermissions,
          actions: actionsFromDraft(res.effectivePermissions),
        });
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load permissions');
        }
      } finally {
        if (!cancelled) setLoadingPerm(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId, allowed]);

  const setFeatureFromUi = (key: FeatureKey, tier: AccessTier, actions: ActionFlags) => {
    setPerm((prev) => applyFeature(prev, key, tier, actions));
  };

  const handleTierChange = (key: FeatureKey, tier: AccessTier) => {
    if (tier === 'off') {
      setFeatureFromUi(key, 'off', { create: false, edit: false, delete: false });
      return;
    }
    if (tier === 'admin') {
      setFeatureFromUi(key, 'admin', { create: false, edit: false, delete: false });
      return;
    }
    setPerm((prev) => {
      const wasAccess = levelToUi(prev.draft[key]).tier === 'access';
      const nextActions = wasAccess
        ? prev.actions[key]
        : { create: false, edit: false, delete: false };
      return applyFeature(prev, key, 'access', nextActions);
    });
  };

  const handleActionToggle = (key: FeatureKey, field: keyof ActionFlags, checked: boolean) => {
    setPerm((prev) => {
      if (levelToUi(prev.draft[key]).tier !== 'access') return prev;
      const nextActions = { ...prev.actions[key], [field]: checked };
      return applyFeature(prev, key, 'access', nextActions);
    });
  };

  const handleSave = async () => {
    if (!selectedId || isAdminRole) return;
    setSaving(true);
    setError(null);
    try {
      const res = await putRolePermissions(selectedId, perm.draft);
      setPerm((prev) => ({
        draft: res.effectivePermissions,
        actions: prev.actions,
      }));
      showToast('Permissions saved.');
    } catch (err) {
      console.error(err);
      if (err instanceof ApiError && err.status === 400) {
        setError('Some permission values are invalid. Please check and try again.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to save permissions');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!allowed) {
    return <Navigate to="/dashboard" replace />;
  }

  const { draft, actions: featureActions } = perm;

  return (
    <div className="role-permissions-page mx-auto px-2 px-md-0">
      <DashboardPageHeader
        title="Role permissions"
        icon="bi bi-sliders"
        description={
          <p className="mb-0">
            Use <strong>Off</strong>, <strong>Access</strong>, or <strong>Admin</strong>. With <strong>Access</strong>, reading
            is automatic; tick <strong>Create</strong>, <strong>Edit</strong>, or <strong>Delete</strong> to allow those actions
            (saved as view / edit / delete in the API).
          </p>
        }
      />

      <div className="role-permissions-page__toolbar mb-3 mb-md-4">
        <div className="row g-3 align-items-end">
          <div className="col-12 col-md">
            <label className="form-label small text-muted mb-1" htmlFor="perm-role-select">
              Role
            </label>
            <select
              id="perm-role-select"
              className="form-select"
              value={selectedId ?? ''}
              onChange={(e) => setSelectedId(e.target.value || null)}
              disabled={loadingList || roles.length === 0}
            >
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.displayName} ({r.name})
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-auto d-grid d-md-block">
            <button
              type="button"
              className="btn btn-success role-permissions-page__save px-4"
              disabled={saving || loadingPerm || !selectedId || isAdminRole}
              onClick={() => void handleSave()}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                  Saving…
                </>
              ) : (
                <>
                  <i className="bi bi-check-lg me-2" aria-hidden />
                  Save changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {isAdminRole && (
        <div className="alert alert-info border-0 shadow-sm mb-3" role="status">
          <div className="d-flex gap-2">
            <i className="bi bi-info-circle flex-shrink-0 mt-1" aria-hidden />
            <div>
              The built-in <strong>ADMIN</strong> role always has full access. Settings below are read-only.
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          <i className="bi bi-exclamation-triangle me-2" aria-hidden />
          {error}
        </div>
      )}

      <Card title="Feature access" icon="bi bi-grid-3x3-gap">
        {loadingList ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted small mt-3 mb-0">Loading roles…</p>
          </div>
        ) : roles.length === 0 ? (
          <p className="text-muted mb-0">No roles found. Create a role on the Roles page first.</p>
        ) : loadingPerm ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading permissions...</span>
            </div>
            <p className="text-muted small mt-3 mb-0">Loading permissions…</p>
          </div>
        ) : (
          <div className="role-permissions-page__matrix">
            {FEATURE_KEYS.map((key) => {
              const ui = levelToUi(draft[key]);
              const disabled = isAdminRole;
              const actionsDisabled = disabled || ui.tier === 'off' || ui.tier === 'admin';
              const accessDisabled = disabled || ui.tier === 'off' || ui.tier === 'admin';

              return (
                <div key={key} className="role-permissions-page__row">
                  <div className="row g-3">
                    <div className="col-12 col-lg-3">
                      <div className="fw-semibold text-body">{FEATURE_LABELS[key]}</div>
                      <div className="small text-muted mt-1">
                        Effective: <span className="text-body">{draft[key]}</span>
                      </div>
                    </div>
                    <div className="col-12 col-lg-9">
                      <div
                        className="role-permissions-page__tier"
                        role="radiogroup"
                        aria-label={`${FEATURE_LABELS[key]} access mode`}
                      >
                        {(['off', 'access', 'admin'] as const).map((tier) => {
                          const inputId = `perm-${key}-tier-${tier}`;
                          const checked = ui.tier === tier;
                          return (
                            <React.Fragment key={tier}>
                              <input
                                type="radio"
                                className="btn-check"
                                name={`perm-${key}-tier`}
                                id={inputId}
                                autoComplete="off"
                                checked={checked}
                                disabled={disabled}
                                onChange={() => handleTierChange(key, tier)}
                              />
                              <label
                                className={`btn btn-sm ${
                                  tier === 'off'
                                    ? 'btn-outline-secondary'
                                    : tier === 'access'
                                      ? 'btn-outline-primary'
                                      : 'btn-outline-success'
                                }`}
                                htmlFor={inputId}
                              >
                                {tier === 'off' ? 'Off' : tier === 'access' ? 'Access' : 'Admin'}
                              </label>
                            </React.Fragment>
                          );
                        })}
                      </div>

                      <div
                        className={`role-permissions-page__access-panel mt-3 ${
                          accessDisabled ? 'role-permissions-page__access-panel--disabled' : ''
                        }`}
                      >
                        <fieldset className="border-0 p-0 m-0">
                          <legend className="form-label small fw-semibold mb-2">Actions allowed</legend>
                          <p className="small text-muted mb-2 mb-md-3">
                            {accessDisabled
                              ? 'Select Access above to choose actions. View access is included automatically when Access is on.'
                              : 'View (read-only) is automatic. Tick any combination of create, edit, or delete.'}
                          </p>
                          <div className="d-flex flex-wrap gap-3">
                            {(
                              [
                                ['create', 'Create'],
                                ['edit', 'Edit'],
                                ['delete', 'Delete'],
                              ] as const
                            ).map(([field, label]) => {
                              const id = `perm-${key}-act-${field}`;
                              return (
                                <div key={field} className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={id}
                                    checked={featureActions[key][field]}
                                    disabled={actionsDisabled}
                                    onChange={(e) =>
                                      handleActionToggle(key, field, e.target.checked)
                                    }
                                  />
                                  <label className="form-check-label small" htmlFor={id}>
                                    {label}
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        </fieldset>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default RolePermissionsPage;
